import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FiCheckCircle, FiLoader, FiMail, FiX } from "react-icons/fi";
import Button from "components/Button";
import FormTextInput from "components/form/FormTextInput";
import api from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { validateEmail } from "lib/validation";
import { isSuccessResponse } from "routes/auth";

export default function ForgotPasswordResetPanel({ defaultEmail, onHide }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [serverMessage, setServerMessage] = useState("");

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    setIsLoading(true);
    try {
      const resp = await api.post(
        API_ENDPOINTS.forgotPassword,
        { email: email.trim() },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't send reset link";
        setError(msg);
        await swtAlert.error("Reset link failed", msg);
        return;
      }

      setSentTo(email.trim());
      setServerMessage(
        resp?.message ||
          "If that email is registered, a password reset link has been sent."
      );
      await swtAlert.success("Reset link sent", resp?.message || "Reset link sent");
    } catch (err) {
      const msg = err?.message || "Couldn't send reset link";
      setError(msg);
      await swtAlert.error("Reset link failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-900">Reset via email</h2>
          <p className="mt-1 text-sm text-text-500">
            We&apos;ll send a reset link to your account email. Use it to set a new
            password if you don&apos;t remember your current one.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-text-500 transition hover:bg-white hover:text-text-900"
          onClick={onHide}
          aria-label="Hide forgot password"
        >
          <FiX className="h-4 w-4" aria-hidden />
          Hide
        </button>
      </div>

      {sentTo ? (
        <div className="mt-4 space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <div className="flex items-start gap-2">
            <FiCheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            <div>
              <p className="font-medium">Check your email</p>
              <p className="mt-1">{serverMessage}</p>
              <p className="mt-2 text-xs text-emerald-800">
                We sent the link to <span className="font-medium">{sentTo}</span>.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setSentTo("");
              setServerMessage("");
            }}
          >
            Send again
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3" noValidate>
          <FormTextInput
            label="Email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            error={error}
            disabled={isLoading}
            autoComplete="email"
          />
          <Button
            type="submit"
            className="inline-flex items-center gap-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiLoader className="h-4 w-4 animate-spin" aria-hidden />
                Sending link…
              </>
            ) : (
              <>
                <FiMail className="h-4 w-4" aria-hidden />
                Send reset link
              </>
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

ForgotPasswordResetPanel.propTypes = {
  defaultEmail: PropTypes.string,
  onHide: PropTypes.func.isRequired,
};
